import { Routes } from '@angular/router';
import {
  mandantAdminRoleGuard,
  customerAreaRedirectGuard,
  CustomerDbUploadGuard,
  customerRoleGuard,
  firstAdminLoginPreventLeaveGuard,
  hasPayrollGuard,
  customerAdminRoleGuard,
} from '@zvoove-market/shared';
import { CustomerContactDetailBreadcrumb } from './customer-contacts/detail/customer-contact-detail.breadcrumb';
import { CustomerUserDetailBreadcrumb } from './customer-users/detail/customer-user-detail.breadcrumb';

export const customerRoutes: Routes = [
  {
    path: 'customer',
    canActivate: [customerRoleGuard()],
    loadComponent: () => import('./shell/customer-shell.page').then((c) => c.CustomerShellPage),
    data: {
      breadcrumb: () => $localize`:@@shell.customerArea:Kundenbereich`,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [customerAreaRedirectGuard()],
        children: [], // either (load)component or children is required
      },
      {
        path: 'detail',
        canActivate: [mandantAdminRoleGuard()],
        canDeactivate: [firstAdminLoginPreventLeaveGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.businessData:Unternehmensdaten`,
        },
        loadComponent: () => import('./customer/customer-detail.page').then((m) => m.CustomerDetailPage),
      },
      {
        canActivate: [mandantAdminRoleGuard(), CustomerDbUploadGuard],
        path: 'db-upload',
        data: {
          breadcrumb: () => $localize`:@@general.dbUpload:Datenbank Hochladen`,
        },
        loadComponent: () => import('./customer-db-upload/customer-db-upload.page').then((c) => c.CustomerDbUploadPage),
      },
      {
        path: 'contacts',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.contacts:Kontakte`,
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./customer-contacts/list/customer-contact-list.page').then((c) => c.CustomerContactListPage),
          },
          {
            path: 'add',
            loadComponent: () => import('./customer-contacts/detail/customer-contact-detail.page').then((c) => c.CustomerContactDetailPage),
            data: {
              breadcrumb: () => $localize`:@@general.add:Hinzufügen`,
            },
          },
          {
            path: ':customerContactId',
            loadComponent: () => import('./customer-contacts/detail/customer-contact-detail.page').then((c) => c.CustomerContactDetailPage),
            data: {
              breadcrumb: CustomerContactDetailBreadcrumb,
            },
          },
        ],
      },
      {
        path: 'activities',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.customerActivityRecords:Tätigkeitsnachweise`,
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./customer-activities/list/customer-activity-list.page').then((c) => c.CustomerActivityListPage),
          },
          {
            path: ':recordId',
            data: {
              breadcrumb: () => $localize`:@@general.activities:Tätigkeiten`,
            },
            loadComponent: () =>
              import('./customer-activities/detail/customer-activity-detail.page').then((c) => c.CustomerActivityDetailPage),
          },
        ],
      },
      {
        path: 'users',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.users:Benutzer`,
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./customer-users/list/customer-user-list.page').then((c) => c.CustomerUserListPage),
          },
          {
            path: 'invite',
            loadComponent: () => import('./customer-users/detail/customer-user-detail.page').then((c) => c.CustomerUserDetailPage),
            data: {
              breadcrumb: () => $localize`:@@customer.inviteUser:Benutzer einladen`,
            },
          },
          {
            path: ':userId',
            loadComponent: () => import('./customer-users/detail/customer-user-detail.page').then((c) => c.CustomerUserDetailPage),
            data: {
              breadcrumb: CustomerUserDetailBreadcrumb,
            },
          },
        ],
      },
      {
        path: 'invoices',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.invoices:Rechnungen`,
        },
        loadComponent: () => import('./customer-orders-and-invoices/invoices/invoices.page').then((m) => m.InvoicesPage),
      },
      {
        path: 'orders',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.orders:Bestellungen`,
        },
        loadComponent: () => import('./customer-orders-and-invoices/orders/orders.page').then((m) => m.OrdersPage),
      },
      {
        path: 'email-accounts',
        canActivate: [customerAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.emailAccounts:E-Mail Konten`,
        },
        loadChildren: () => import('./customer-email/accounts/routes').then((r) => r.CUSTOMER_EMAIL_ROUTES),
      },
      {
        path: 'email-logs',
        canActivate: [customerAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@email.sentMessages:Versendete Nachrichten`,
        },
        loadComponent: () => import('./customer-email/email-logs/email-logs.page').then((p) => p.CustomerEmailLogsPage),
      },
      {
        canActivate: [mandantAdminRoleGuard(), hasPayrollGuard()],
        path: 'payroll-token',
        data: {
          breadcrumb: () => $localize`:@@general.payrollToken:Payrolltoken`,
        },
        loadComponent: () => import('./payroll-token/payroll-token.page').then((m) => m.PayrollTokenPage),
      },
      {
        path: 'appointments',
        data: {
          breadcrumb: () => $localize`:@@general.appointments:Termine`,
        },
        loadComponent: () => import('./appointments/appointment-list.page').then((m) => m.AppointmentListPage),
      },
      {
        path: 'token',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@customer.pdlToken:PDL Token`,
        },
        loadChildren: () => import('./customer-token/customer-token.routes').then((m) => m.CUSTOMER_TOKEN_ROUTES),
      },
      {
        path: 'digital-signature',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.digitalSignature:Digitale Signatur`,
        },
        loadComponent: () => import('./digital-signature/digital-signature.page').then((p) => p.DigitalSignaturePageComponent),
      },
      {
        path: 'adv-contracts',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@customer.advContracts:ADV-Verträge`,
        },
        loadComponent: () => import('./adv-contracts/adv-contract-list.page').then((m) => m.AdvContractListPage),
      },
      {
        canActivate: [mandantAdminRoleGuard()],
        path: 'partner-management',
        data: {
          breadcrumb: () => $localize`:@@general.partnerManagement:Partner Management`,
        },
        loadComponent: () => import('./partner-management/partner-management.page').then((m) => m.PartnerManagementPage),
      },
      {
        path: 'api-key',
        canActivate: [mandantAdminRoleGuard()],
        data: {
          breadcrumb: () => $localize`:@@general.apikey:API-Schlüssel`,
        },
        loadChildren: () => import('./customer-api-key/customer-api-key.routes').then((m) => m.CUSTOMER_APIKEY_ROUTES),
      },
    ],
  },
];
